/**
 * src/shell/save/export-import.ts — 存档导出/导入（设计稿 §5.1.6）。
 *
 * 铁律实现：导出包 = 7 键（bundle 6 + candidates）+ meta 头。
 * apikey 存在完全独立的键里，本模块根本不读它——导出/dump 天然无泄漏面；
 * 引擎 tp3:* 键（会话/事件/产物）不进导出包（引擎 §8.2）。
 * 导入：四道校验闸门全过 → 先自动备份当前存档 → 整体替换 → 全量广播。
 */

import { CANDIDATES_KEY } from './keys'
import { localSaveStore, restoreLatestBackup } from './store'
import { validateImport, type ImportFileShape } from './validate'
import type { CandidatesFile } from './seed'

export { restoreLatestBackup }

/** 组装导出对象（7 键顶层平铺 + meta 头；绝不包含 apikey——铁律） */
export async function buildExport(): Promise<ImportFileShape> {
  const bundle = await localSaveStore.readAll()
  const candidatesRaw = localStorage.getItem(CANDIDATES_KEY)
  const candidates: CandidatesFile = candidatesRaw
    ? (JSON.parse(candidatesRaw) as CandidatesFile)
    : { candidates: [] }
  const company = bundle.save?.company_name ?? 'unknown'
  return {
    meta: {
      game: 'third-play',
      bundleVersion: 1,
      exportedAt: new Date().toISOString(),
      appVersion: `v2-shell (${company})`,
    },
    save: bundle.save,
    projects: bundle.projects,
    decisions: bundle.decisions,
    reviews: bundle.reviews,
    employees: bundle.employees,
    market: bundle.market,
    candidates,
  }
}

/** 触发浏览器下载 */
export async function downloadExport(): Promise<string> {
  const data = await buildExport()
  const company = data.save?.company_name ?? 'thirdplay'
  const date = new Date().toISOString().slice(0, 10)
  const filename = `thirdplay-save-${company}-${date}.json`
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
  return filename
}

export type ImportResult =
  | { ok: true }
  | { ok: false; errors: string[] }

/** 导入：校验闸门 → 自动备份当前 → 整体替换 7 键 → 广播刷新 */
export async function importFromText(jsonText: string): Promise<ImportResult> {
  let raw: unknown
  try {
    raw = JSON.parse(jsonText)
  } catch {
    return { ok: false, errors: ['文件不是合法 JSON。'] }
  }
  const result = validateImport(raw)
  if (!result.ok) return { ok: false, errors: result.errors }
  const { save, projects, decisions, reviews, employees, market, candidates } = result.data

  // 自动备份当前存档（导入前保险）
  const current = await localSaveStore.readAll()
  const currentCandidates = localStorage.getItem(CANDIDATES_KEY)
  try {
    localStorage.setItem('thirdplay:v2:pre-import-backup', JSON.stringify({
      ts: new Date().toISOString(),
      bundle: current,
      candidates: currentCandidates ? JSON.parse(currentCandidates) : { candidates: [] },
    }))
  } catch (err) {
    console.warn('[shell/save] 导入前备份失败（继续导入）：', err)
  }

  // 候选与批次同一失败域（M4 整改顺带项，与 hireCandidate 同法）：
  // 候选单键 setItem 天然原子先写；批次失败（writeBatch 已自回滚 bundle 键）→
  // 候选也回旧值——任何失败路径都停在导入前的完整旧状态。
  // 残余病理窗口（回滚写本身也失败）由上方 pre-import-backup 兜底。
  const oldCandidates = localStorage.getItem(CANDIDATES_KEY)
  try {
    localStorage.setItem(CANDIDATES_KEY, JSON.stringify(candidates))
    await localSaveStore.writeBatch({ save, projects, decisions, reviews, employees, market })
  } catch (err) {
    if (oldCandidates === null) localStorage.removeItem(CANDIDATES_KEY)
    else localStorage.setItem(CANDIDATES_KEY, oldCandidates)
    console.error('[shell/save] 导入写入失败（已回滚到导入前状态）：', err)
    return { ok: false, errors: ['写入存档失败（存储空间不足？），已回滚到导入前状态。'] }
  }
  return { ok: true }
}
